import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { appointmentService as api, appointmentError, orderedAppointments, displayTime } from '../../services/appointmentService';
import ProviderAvailability from './ProviderAvailability';
import './appointments.css';

export default function AppointmentCalendar({ provider = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(orderedAppointments(await api.mine())); }
    catch (e) { setError(appointmentError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  async function change(id, status) {
    if (busy) return;
    setBusy(true); setError('');
    try { await api.status(id, status, reason.trim()); setCancelId(null); setReason(''); await refresh(); }
    catch (e) { setError(appointmentError(e)); if (e?.response?.status === 409) await refresh(); }
    finally { setBusy(false); }
  }
  return <main className="appointments">
    <Link to={provider ? '/service-provider/dashboard' : '/dashboard'}>Back to dashboard</Link>
    <h1>{provider ? 'Calendar' : 'My Calendar'}</h1>
    <p>Appointment times below use your device timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
    {provider && <ProviderAvailability />}
    <h2>Appointments</h2>
    <label>Filter by date <input type="date" value={date} onChange={e => setDate(e.target.value)} /></label> <button onClick={() => setDate('')}>Show all dates</button> <button disabled={loading || busy} onClick={() => { setError(''); refresh(); }}>Refresh appointments</button>
    {error && <p role="alert" className="appointment-error">{error}</p>}
    {loading ? <p role="status">Loading appointments…</p> : <>
      {!rows.length && <p>No appointments yet.</p>}
      <ol className="appointment-list">{rows.filter(a => !date || new Date(a.startAt).toLocaleDateString('sv-SE') === date).map(a => {
        const id = a.appointmentId || a.id;
        const name = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ');
        return <li key={id}><h3>{name(a.provider) || `Provider #${a.providerId}`}</h3>
          {provider && <p>Customer: {name(a.customer) || `Customer #${a.userId}`}</p>}
          <p>{a.event?.eventTitle || (a.eventId ? `Event #${a.eventId}` : 'No linked event')}</p>
          <p><time dateTime={a.startAt}>{displayTime(a.startAt)}</time> – {displayTime(a.endAt)}</p>
          <p>Status: <strong>{a.status}</strong> · Provider timezone: {a.timezone}</p>
          <small>Appointment ID: {id}</small>
          {a.cancellationReason && <p>Cancellation reason: {a.cancellationReason}</p>}
          {a.status === 'confirmed' && <div className="appointment-actions">
            <button disabled={busy} onClick={() => { setCancelId(id); setReason(''); }}>Cancel appointment</button>
            {provider && <button disabled={busy || Date.parse(a.endAt) > Date.now()} onClick={() => change(id, 'completed')}>Mark completed</button>}
          </div>}
          {cancelId === id && <form onSubmit={e => { e.preventDefault(); change(id, 'cancelled'); }}>
            <label>Cancellation reason <textarea required maxLength={500} value={reason} onChange={e => setReason(e.target.value)} /></label>
            <button disabled={busy || !reason.trim()} type="submit">Confirm cancellation</button> <button type="button" disabled={busy} onClick={() => setCancelId(null)}>Keep appointment</button>
          </form>}
        </li>;
      })}</ol>
    </>}
  </main>;
}
